import { ResponsiveSection, Stack, type BoxProps } from '@stitch-fix/mode-react';
import { LazyMotion, MotionConfig } from 'framer-motion';
import { useEffect } from 'react';
import { addFeatureFlagsToRumContext } from '../../utils/loggers/browser';
import { AccordionGroup, type AccordionGroupProps } from '../AccordionGroup';
import { DisplayCarousel, type DisplayCarouselProps } from '../DisplayCarousel';
import { DisplayGrid, type DisplayGridProps } from '../DisplayGrid';
import { DisplayText, type DisplayTextProps } from '../DisplayText';
import { InteractiveGrid, type InteractiveGridProps } from '../InteractiveGrid';
import { StickyCta, type StickyCtaProps } from '../StickyCta';
import { Layout, type LayoutProps } from '../Layout';
import { SEOData, type SEODataProps } from '../SEOData';
import { Hero, type HeroProps } from '../Hero';
import { type InteractiveCarouselProps, InteractiveCarousel } from '../InteractiveCarousel/InteractiveCarousel';
import { StickyCtaContextProvider, useStickyCtaContext } from '../StickyCta/useStickyCtaContext';
import { DynamicAdsHero, type DynamicAdsHeroProps } from '../../features/dynamicAds/components/DynamicAdsHero/DynamicAdsHero';
import { GalleryHero, type GalleryHeroProps } from '../GalleryHero/GalleryHero';
import { DisplaySplit, type DisplaySplitProps } from '../DisplaySplit/DisplaySplit';
import { AnimatedGrid, type AnimatedGridProps } from '../AnimatedGrid/AnimatedGrid';
import { DisplayPanel, type DisplayPanelProps } from '../DisplayPanel/DisplayPanel';
import { MediaGallery, type MediaGalleryProps } from '../MediaGallery/MediaGallery';
import { getVerticalSpacing } from './getVerticalSpacing/getVerticalSpacing';
import { type SlimBannerProps, toLayoutSlimBannerProps } from '../SlimBanner/SlimBanner';
import { VideoFullBleedHero, type VideoFullBleedHeroProps } from '../VideoLayout';
import { InteractiveImageScrollerHero, type InteractiveImageScrollerHeroProps } from '../InteractiveImageScroller/InteractiveImageScrollerHero';
import { InteractiveImageScroller, type InteractiveImageScrollerProps } from '../InteractiveImageScroller/InteractiveImageScroller';
import { DisplayMedia, type DisplayMediaProps } from '../DisplayMedia/DisplayMedia';
import { ShopCategory, ShopCategoryGridCursorProvider, type ShopCategoryGridPaginationProps, type ShopCategoryProps } from '../../features/collabs/components/ShopCategory/ShopCategory';
import { useShopCategoryGridProps } from './useShopCategoryGridProps';
import { IDmeSignupTypeCookie, type IDmeSignupTypeCookieProps } from '../../features/idme/components/IDmeSignupTypeCookie/IDmeSignupTypeCookie';
export type ModularComponentProps = {
  backgroundColor?: string;
  verticalSpacing?: BoxProps['mt'];
};

// Define each module with its specific props type
type ModuleDefinition = {
  mediaGallery: MediaGalleryProps;
  galleryHero: GalleryHeroProps;
  hero: HeroProps;
  dynamicAdsHero: DynamicAdsHeroProps;
  interactiveImageScrollerHero: InteractiveImageScrollerHeroProps;
  interactiveImageScroller: InteractiveImageScrollerProps;
  displayGrid: DisplayGridProps;
  displayCarousel: DisplayCarouselProps;
  displayMedia: DisplayMediaProps;
  displaySplit: DisplaySplitProps;
  interactiveGrid: InteractiveGridProps;
  interactiveCarousel: InteractiveCarouselProps;
  displayText: DisplayTextProps;
  accordionGroup: AccordionGroupProps;
  stickyCta: StickyCtaProps;
  animatedGrid: AnimatedGridProps;
  displayPanel: DisplayPanelProps;
  slimBanner: SlimBannerProps;
  videoFullBleedHero: VideoFullBleedHeroProps;
  shopCategoryGrid: ShopCategoryProps;
  idMe: IDmeSignupTypeCookieProps;
};

// Map over ModuleDefinition to include ModularComponentProps in each module's props
export type ModularComponent = { [K in keyof ModuleDefinition]: {
  type: K;
  props: ModuleDefinition[K] & ModularComponentProps;
} }[keyof ModuleDefinition] | {
  type: 'notSupported';
  props: null;
};
type ShopCategoryGridModule = Extract<ModularComponent, {
  type: 'shopCategoryGrid';
}>;

// ModularComponent after useShopCategoryGridProps injects pagination state
export type RenderableModularComponent = Exclude<ModularComponent, ShopCategoryGridModule> | {
  type: 'shopCategoryGrid';
  props: ShopCategoryGridModule['props'] & ShopCategoryGridPaginationProps;
};
export type ModularPageWrapperProps = {
  seoData: SEODataProps;
  modules: ModularComponent[];
  viewEventTrackingName?: LayoutProps['viewEventTrackingName'];
  jsonLdData?: SEODataProps['jsonLdData'];
  featureFlags?: Parameters<typeof addFeatureFlagsToRumContext>[0];
  forceHideHeaderFooter?: boolean;
  children?: React.ReactNode;
  childrenPlacement?: 'beforeModules' | 'afterModules';
};
const modularComponentResolver = (module: RenderableModularComponent) => {
  if (module.type === 'hero') {
    return <Hero {...module.props} />;
  }
  if (module.type === 'dynamicAdsHero') {
    return <DynamicAdsHero {...module.props} />;
  }
  if (module.type === 'interactiveImageScroller') {
    return <InteractiveImageScroller {...module.props} />;
  }
  if (module.type === 'interactiveImageScrollerHero') {
    return <InteractiveImageScrollerHero {...module.props} />;
  }
  if (module.type === 'videoFullBleedHero') {
    return <VideoFullBleedHero {...module.props} />;
  }
  if (module.type === 'displayGrid') {
    return <DisplayGrid {...module.props} />;
  }
  if (module.type === 'displayMedia') {
    return <DisplayMedia {...module.props} />;
  }
  if (module.type === 'displayCarousel') {
    return <DisplayCarousel {...module.props} />;
  }
  if (module.type === 'interactiveGrid') {
    return <InteractiveGrid {...module.props} />;
  }
  if (module.type === 'interactiveCarousel') {
    return <InteractiveCarousel {...module.props} />;
  }
  if (module.type === 'displayText') {
    return <DisplayText {...module.props} />;
  }
  if (module.type === 'accordionGroup') {
    return <AccordionGroup {...module.props} />;
  }
  if (module.type === 'stickyCta') {
    return <StickyCta {...module.props} />;
  }
  if (module.type === 'galleryHero') {
    return <GalleryHero {...module.props} />;
  }
  if (module.type === 'displaySplit') {
    return <DisplaySplit {...module.props} />;
  }
  if (module.type === 'animatedGrid') {
    return <AnimatedGrid {...module.props} />;
  }
  if (module.type === 'displayPanel') {
    return <DisplayPanel {...module.props} />;
  }
  if (module.type === 'mediaGallery') {
    return <MediaGallery {...module.props} />;
  }
  if (module.type === 'shopCategoryGrid') {
    return <ShopCategory {...module.props} />;
  }
  return null;
};
export const ModulePageComponents = ({
  modules
}: {
  modules: ModularComponent[];
}) => {
  const modulesWithShopCategoryGridProps = useShopCategoryGridProps(modules);
  const idMeModule = modulesWithShopCategoryGridProps.find(module => module.type === 'idMe');
  const renderableModules = modulesWithShopCategoryGridProps.filter(
  // We don't want to render slimBanner modules because they are handled by the Layout component.
  // The ID.me module writes a cookie without rendering UI, so we should exclude it as well.
  module => module.type !== 'slimBanner' && module.type !== 'idMe');
  const modulesWithSpacing = getVerticalSpacing(renderableModules);
  return <>
      {idMeModule && <IDmeSignupTypeCookie {...idMeModule.props} />}

      <Stack spacing={{
      sm: '3rem'
    }}>
        {modulesWithSpacing.map((module, index) => {
        const key = `${module.type}-${index}`;
        return <div key={key} data-testid={module.type}>
              {modularComponentResolver(module)}
            </div>;
      })}
      </Stack>
    </>;
};
const ModulePageRenderer = ({
  seoData,
  modules,
  viewEventTrackingName,
  jsonLdData,
  featureFlags,
  forceHideHeaderFooter,
  children,
  childrenPlacement
}: Pick<ModularPageWrapperProps, 'modules' | 'seoData' | 'viewEventTrackingName' | 'jsonLdData' | 'featureFlags' | 'forceHideHeaderFooter' | 'children' | 'childrenPlacement'>) => {
  const {
    setStickyContainerTarget
  } = useStickyCtaContext();
  const slimBannerModule = modules.find(module => module.type === 'slimBanner');
  const customSlimBanner = slimBannerModule ? toLayoutSlimBannerProps(slimBannerModule.props) : undefined;
  useEffect(() => {
    if (!featureFlags) return;
    addFeatureFlagsToRumContext(featureFlags);
  }, [featureFlags]);

  // MotionConfig provides accessibility support for all child components with animations
  // by respecting user preferences for reduced motion (prefers-reduced-motion CSS media query)
  return <MotionConfig reducedMotion="user">
      <LazyMotion features={() => import('framer-motion').then(mod => mod.domAnimation)}>
        <Layout viewEventTrackingName={viewEventTrackingName} footerRef={setStickyContainerTarget} customSlimBanner={customSlimBanner} forceHideHeaderFooter={forceHideHeaderFooter}>
          <ResponsiveSection mt="0" width={{
          sm: 'full-bleed'
        }} gutter="none">
            <SEOData {...seoData} jsonLdData={jsonLdData} />
            {childrenPlacement === 'beforeModules' && children}
            <ModulePageComponents modules={modules} />
            {childrenPlacement === 'afterModules' && children}
          </ResponsiveSection>
        </Layout>
      </LazyMotion>
    </MotionConfig>;
};
export const ModularPageWrapper = ({
  seoData,
  modules,
  viewEventTrackingName = 'landing',
  jsonLdData,
  featureFlags,
  forceHideHeaderFooter = false,
  children,
  childrenPlacement = 'afterModules'
}: ModularPageWrapperProps) => {
  return <StickyCtaContextProvider>
      <ShopCategoryGridCursorProvider>
        <ModulePageRenderer seoData={seoData} modules={modules} viewEventTrackingName={viewEventTrackingName} jsonLdData={jsonLdData} featureFlags={featureFlags} forceHideHeaderFooter={forceHideHeaderFooter} childrenPlacement={childrenPlacement}>
          {children}
        </ModulePageRenderer>
      </ShopCategoryGridCursorProvider>
    </StickyCtaContextProvider>;
};